import {
    Filter,
} from './filter';

/**
 * Selectable list suitable for Redux
 * @typeparam T Type of the values
 */
export type SelectList<T> = {
    /**
     * List of values (stored as an array)
     */
    values: T[];

    /**
     * Value selected from the list
     */
    selected?: T;
};

/**
 * Function to set the list values
 * @param list 
 * List instance
 * @param values 
 * Values to be set
 * @returns 
 * A new list instance
 * @typeparam T 
 * Type of the values
 */
export const init = <T>(
    list: SelectList<T>,
    values: T[],
): SelectList<T> => {
    const nxt: SelectList<T> = {
        values,
        selected: undefined,
    };
    return nxt;
};

/**
 * Function to set the selected value
 * @param list 
 * List instance
 * @param filter 
 * Filter function to select the value to be
 * set as selected
 * @returns
 * A new list instance
 * @typeparam T 
 * Type of the values
 */
export const select = <T>(
    list: SelectList<T>,
    filter: Filter<T>,
): SelectList<T> => {
    const {
        values,
    } = list;

    let selected: T;
    for (const value of values) {
        if (filter(value)) {
            selected = value;
            break;
        }
    }
    const nxt: SelectList<T> = {
        ...list,
        selected,
    };
    return nxt;
}

/**
 * Function to unset the selected value
 * @param list 
 * List instance
 * @returns
 * A new list instance with selected attribute 
 * set to undefined 
 * @typeparam T 
 * Type of the values
 */
export const unselect = <T>(
    list: SelectList<T>,
): SelectList<T> => {
    const nxt: SelectList<T> = {
        ...list,
        selected: undefined,
    };
    return nxt;
}

/**
 * Function to add a value to the list
 * @param list
 * List instance
 * @param value
 * Value to be added to the list
 * @returns
 * A new list instance with the added value
 * @typeparam T 
 * Type of the values
 */
export const add = <T>(
    list: SelectList<T>,
    value: T,
): SelectList<T> => {
    const nxt: SelectList<T> = {
        ...list,
        values: [
            ...list.values,
            value,
        ]
    }
    return nxt;
};

/**
 * Function to remove a value from the list
 * @param list
 * List instance
 * @param filter
 * Filter function to select the value to be
 * removed
 * @returns
 * A new list instance with the added value
 * @typeparam T 
 * Type of the values
 */
export const remove = <T>(
    list: SelectList<T>,
    filter: Filter<T>,
): SelectList<T> => {
    const {
        values,
        selected,
    } = list;

    let selection = selected;
    const retained: T[] = [];
    for (const value of values) {
        if (!(filter(value))) {
            retained.push(value);
        } else {
            if (selected === value) {
                selection = undefined;
            }
        }
    }

    const nxt: SelectList<T> = {
        ...list,
        values: retained,
        selected: selection,
    };
    return nxt;
};
